import React, { Component } from 'react';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';

import { OutsideClickHandler } from '../../../components';

import FilterForm from '../FilterForm/FilterForm';
import IconPlus from '../IconPlus/IconPlus';

import css from './FilterPopupForSidebar.module.css';

/**
 * FilterPopupForSidebar component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.popupClassName] - Custom class that overrides the default class css.popupSize
 * @param {string} props.id - The ID
 * @param {React.Node} props.label - The label
 * @param {boolean} props.isSelected - Whether the filter is selected
 * @param {boolean} props.keepDirtyOnReinitialize - Whether to keep the dirty state on reinitialize
 * @param {number} props.contentPlacementOffset - The content placement offset
 * @param {React.Node} props.children - The children
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to submit
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
class FilterPopupForSidebar extends Component {
  constructor(props) {
    super(props);

    this.state = { isOpen: false };
    this.filter = null;
    this.filterContent = null;

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.toggleOpen = this.toggleOpen.bind(this);
    this.positionStyleForContent = this.positionStyleForContent.bind(this);
  }

  handleSubmit(values) {
    const { onSubmit } = this.props;
    this.setState({ isOpen: false });
    const button = document.getElementById(`${this.props.id}.toggle`);
    if (button) {
      button.focus();
    }

    onSubmit(values);
  }

  handleClear() {
    const { onSubmit, onClear } = this.props;
    this.setState({ isOpen: false });

    const button = document.getElementById(`${this.props.id}.toggle`);
    if (button) {
      button.focus();
    }

    if (onClear) {
      onClear();
    }

    onSubmit(null);
  }

  handleCancel() {
    const { onSubmit, onCancel, initialValues } = this.props;
    this.setState({ isOpen: false });

    const button = document.getElementById(`${this.props.id}.toggle`);
    if (button) {
      button.focus();
    }

    if (onCancel) {
      onCancel();
    }

    onSubmit(initialValues);
  }

  handleBlur() {
    this.setState({ isOpen: false });
  }

  handleKeyDown(e) {
    // Gather all escape presses to close menu
    if (e.key === 'Escape') {
      const button = document.getElementById(`${this.props.id}.toggle`);
      if (button) {
        button.focus();
      }

      this.toggleOpen(false);
    } else if (e.key === 'ArrowDown' && this.filter.contains(e.target) && !this.state.isOpen) {
      e.preventDefault();
      e.stopPropagation();
      this.toggleOpen(true);
    } else if (e.key === 'ArrowUp' && this.filter.contains(e.target) && this.state.isOpen) {
      e.preventDefault();
      e.stopPropagation();
      this.toggleOpen(false);
    }
  }

  toggleOpen(enforcedState) {
    const callback = () => {
      const form = document.getElementById(`${this.props.id}.form`);
      const currentDate = form?.querySelector('[data-current="true"]');
      if (this.state.isOpen) {
        window.setTimeout(() => currentDate?.focus(), 100);
      }
    };
    if (enforcedState) {
      this.setState({ isOpen: enforcedState }, callback);
    } else {
      this.setState(prevState => ({ isOpen: !prevState.isOpen }), callback);
    }
  }

  positionStyleForContent() {
    // No-op: the popup renders in normal document flow now (see .popup in
    // FilterPopupForSidebar.module.css), so it doesn't need a computed
    // left/right/minWidth offset the way an absolutely-positioned overlay
    // would.
    return {};
  }

  render() {
    const {
      rootClassName,
      className,
      popupClassName,
      id,
      label,
      labelSelection,
      isSelected,
      children,
      initialValues,
      keepDirtyOnReinitialize = false,
      ariaLabel,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);
    const popupClasses = classNames(css.popup, { [css.isOpen]: this.state.isOpen });
    const popupSizeClasses = popupClassName || css.popupSize;
    const contentStyle = this.positionStyleForContent();
    const formId = `${id}.form`;
    return (
      <OutsideClickHandler className={css.root} onOutsideClick={this.handleBlur}>
        <div
          className={classes}
          onKeyDown={this.handleKeyDown}
          ref={node => {
            this.filter = node;
          }}
        >
          <div className={css.filterHeader}>
            <button
              type="button"
              id={`${id}.toggle`}
              className={css.labelButton}
              onClick={() => this.toggleOpen()}
              aria-label={ariaLabel}
            >
              <span className={css.labelButtonContent}>
                <span className={css.labelWrapper}>
                  <span className={css.label}>
                    {label}
                    {labelSelection ? (
                      <>
                        <span>{': '}</span>
                        <span className={css.labelSelected}>{labelSelection}</span>
                      </>
                    ) : null}
                  </span>
                </span>
                <span className={css.openSign}>
                  <IconPlus isOpen={this.state.isOpen} isSelected={isSelected} />
                </span>
              </span>
            </button>
          </div>
          <div
            id={id}
            className={popupClasses}
            ref={node => {
              this.filterContent = node;
            }}
            style={contentStyle}
          >
            {this.state.isOpen ? (
              <FilterForm
                id={formId}
                paddingClasses={popupSizeClasses}
                showAsPopup
                initialValues={initialValues}
                keepDirtyOnReinitialize={keepDirtyOnReinitialize}
                onSubmit={this.handleSubmit}
                onCancel={this.handleCancel}
                onClear={this.handleClear}
              >
                {children}
              </FilterForm>
            ) : null}
          </div>
        </div>
      </OutsideClickHandler>
    );
  }
}

export default injectIntl(FilterPopupForSidebar);
